import { useCallback, useEffect, useRef, useState } from "react";

import { Button, Spinner, TextField } from "@shapr3d/frontend-lib";
import { useController } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import usePrevious from "../../../../hooks/usePrevious";
import { useTracker } from "../../../../hooks/useTracker";
import { ElementClickAction } from "../../../../lib/TrackingAdapter";
import { SearchParam, ServiceErrorCode } from "../../../../types";
import { useCheckoutFormContext } from "../CheckoutFormProvider";

import style from "./CouponInput.module.css";

export const name = "couponCode";

export default function CouponInput({ isDiscountApplied }: { isDiscountApplied: boolean }) {
  const {
    methods: { control },
    estimateMutationResult: { isPending: isEstimatePending, data } = {},
  } = useCheckoutFormContext();

  const [error, setError] = useState<string | undefined>(undefined);

  const { field } = useController({
    name,
    control,
  });
  const tracker = useTracker();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const searchParamCouponCode = searchParams.get(SearchParam.SubscriptionCoupon);
    if (searchParamCouponCode) {
      searchParams.delete(SearchParam.SubscriptionCoupon);
      setSearchParams(searchParams);
      field.onChange(searchParamCouponCode);
    }
  }, [searchParams, field, setSearchParams]);

  const [isCouponOpen, setIsCouponOpen] = useState(false);

  const previouslyAppliedCouponCode = usePrevious(data?.estimate.appliedCouponCode);
  useEffect(() => {
    if (previouslyAppliedCouponCode && !data?.estimate.appliedCouponCode) {
      setIsCouponOpen(false);
    }

    if (data?.errors?.some((e) => e.code === ServiceErrorCode.InvalidCoupon)) {
      setError("Invalid coupon code");
    }
  }, [data, setError, previouslyAppliedCouponCode]);

  const couponCodeInput = useRef<HTMLInputElement>(null);
  const applyCoupon = useCallback(() => {
    field.onChange(couponCodeInput.current?.value);
  }, [field]);

  return (
    <>
      {!isCouponOpen && !isDiscountApplied && (
        <Button
          variant="borderless"
          className={style.couponButton}
          disabled={isEstimatePending}
          onClick={() => {
            tracker.trackButtonClick({
              action: ElementClickAction.ADD_COUPON_CODE,
            });
            setError(undefined);
            setIsCouponOpen(true);
          }}
        >
          Add coupon code
        </Button>
      )}
      {isDiscountApplied && (
        <Button
          variant="borderless"
          className={style.couponButton}
          disabled={isEstimatePending}
          onClick={() => {
            field.onChange(undefined);
            setError(undefined);
            setIsCouponOpen(false);
          }}
        >
          Remove coupon code
        </Button>
      )}
      {isCouponOpen && !isDiscountApplied && (
        <div className={style.couponInputContainder}>
          <TextField
            autoFocus
            className={style.couponInput}
            placeholder="Enter code"
            ref={couponCodeInput}
            disabled={isEstimatePending}
            errorMessage={!isEstimatePending ? error : undefined}
            onChange={() => {
              setError(undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                applyCoupon();
              }
            }}
            maxLength={100}
          />
          <Button
            className={style.couponInput}
            variant="grayscale"
            disabled={isEstimatePending}
            onClick={() => {
              applyCoupon();
            }}
          >
            {isEstimatePending && <Spinner size={20} />}
            {!isEstimatePending && <span>Apply</span>}
          </Button>
        </div>
      )}
    </>
  );
}
